// src/lib/hubspot.ts
// HubSpot-specific side of the analytics consent gate: the _hsq/_hsp queue
// pushes plus the tag injection. Decision logic lives in lib/consent.ts, React
// wiring in components/organisms/legal/hubspot-tracking.tsx.

const SCRIPT_ID = "hs-script-loader";

declare global {
  interface Window {
    _hsq?: unknown[][];
    _hsp?: unknown[][];
  }
}

export function stopHubspotTracking(): void {
  if (typeof window === "undefined") return;
  window._hsq = window._hsq ?? [];
  window._hsq.push(["doNotTrack"]);
  window._hsp = window._hsp ?? [];
  window._hsp.push(["revokeCookieConsent"]);
}

export function resumeHubspotTracking(): void {
  if (typeof window === "undefined") return;
  window._hsq = window._hsq ?? [];
  window._hsq.push(["doNotTrack", { track: true }]);
}

/**
 * Injects the tracking code, once per document.
 *
 * The `doNotTrack {track:true}` push has to happen *before* the tag boots:
 * "doNotTrack" is persisted by HubSpot in the long-lived __hs_do_not_track
 * cookie, so a visitor who once pressed "Reject all" stays blocked (script
 * loads, no __ptq.gif) on every later page load until tracking is explicitly
 * re-enabled.
 *
 * The tracker sends the first pageview itself on load — do not push one here or
 * it double-counts.
 */
export function loadHubspotTracking(portalId: string): void {
  if (typeof document === "undefined") return;
  resumeHubspotTracking();
  if (document.getElementById(SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.src = `https://js-eu1.hs-scripts.com/${portalId}.js`;
  document.body.appendChild(script);
}

/** Client-side route change: only the first pageview is automatic. */
export function trackHubspotPageView(path: string): void {
  if (typeof window === "undefined") return;
  window._hsq = window._hsq ?? [];
  window._hsq.push(["setPath", path]);
  window._hsq.push(["trackPageView"]);
}
